import { ChangeDetectionStrategy, ChangeDetectorRef, Component, forwardRef, Input } from '@angular/core';
import {
  AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, UntypedFormBuilder,
  UntypedFormGroup, ValidationErrors, Validator, Validators
} from '@angular/forms';
import { format, parseISO } from 'date-fns';
import { cloneDeep as _cloneDeep } from 'lodash-es';
import { startWith, takeUntil } from 'rxjs/operators';

import { FormGroupConfig } from 'core/form-config-type';
import { WithDisposableSubject } from 'core/mixins/with-disposable-subject';
import * as CustomValidators from 'core/validators';
import { AgePipe } from 'shared/pipes/age.pipe';

export interface DOBFormModel {
  age?: string | number;
  dateOfBirth?: string;
}

@Component({
  selector: 'app-dob-form',
  templateUrl: './dob-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DOBFormComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => DOBFormComponent),
      multi: true
    }
  ]
})
export class DOBFormComponent
  extends WithDisposableSubject()
  implements ControlValueAccessor, Validator {

  constructor(
    private _fb: UntypedFormBuilder,
    private _cd: ChangeDetectorRef
    ) {
    super();

    const config: FormGroupConfig<DOBFormModel> = {
      dateOfBirth: [null,
        [Validators.pattern(/^(0[1-9]|1[0-2])\/(0[1-9]|1\d|2\d|3[01])\/(19|20)\d{2}$/), CustomValidators.noFutureDate]
      ],
      age: [null,
        [Validators.max(120), Validators.min(0), CustomValidators.digitsOnly]
      ]
    };

    const fg = this._fb.group(config);

    fg.controls.dateOfBirth
      .valueChanges
      .pipe(
        startWith(fg.controls.dateOfBirth.value),
      )
      .subscribe((value) => {
        const ageCtrl = fg.controls.age;
        if (!value) {
          ageCtrl.enable();
        } else {
          ageCtrl.disable();
          if (value.length === 10) {
            const age = new AgePipe().transform(value);
            ageCtrl.setValue(age);
          }
          setTimeout(() => ageCtrl.disable(), 1);
        }
        this._cd.markForCheck();
      });

    this.formGroup = fg;
  }
  formGroup: UntypedFormGroup;

  @Input() showDOB = true;

  static toFormModel(entity: { dateOfBirth: any; age: any; calculatedBirthDate: boolean }): DOBFormModel {
    return {
      dateOfBirth: (entity?.dateOfBirth && !entity?.calculatedBirthDate) ? format(parseISO(entity.dateOfBirth), 'MM/dd/yyyy') : null,
      age: entity.age
    };
  }

  _onTouched = () => { };

  /** Control Value Accessor Implementmenation */
  writeValue(obj: DOBFormModel): void {
    if (obj) {
      this.formGroup.patchValue(obj, { emitEvent: false });
      if (obj.dateOfBirth) {
        this.formGroup.controls?.age?.disable();
      }
    }
  }

  registerOnChange(fn: any): void {
    this.formGroup.valueChanges.pipe(
      takeUntil(this.disposer)
    ).subscribe(fn);
  }

  registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.formGroup.disable();
    } else {
      this.formGroup.enable();
    }
  }

  /** Validator Implementation */
  validate(control: AbstractControl): ValidationErrors {
    if (this.formGroup.valid) {
      return null;
    }
    return { dob: true };
  }

  registerOnValidatorChange?(fn: () => void): void {
    this.formGroup.valueChanges.pipe(
      takeUntil(this.disposer)
    ).subscribe((v) => {
      fn();
    });
  }

  getValue(): DOBFormComponent {
    return this.formGroup.value;
  }
}
